import { Helmet } from "react-helmet-async";

const SITE_NAME = "Propix3ls";
const PRODUCTION_HOST = "https://propix3ls.com";
const DEFAULT_OG = `${PRODUCTION_HOST}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  "Same-day lifestyle photography, UGC content, and 4K reels for content creators, influencers, and TikTokers. Book in London, NYC, LA, Toronto. Educational e-books to grow your creator brand.";

/**
 * Per-page SEO + structured-data wrapper. Drop one of these at the top of any
 * page component to give it a unique title, description, canonical URL,
 * Open Graph + Twitter cards, and schema.org JSON-LD.
 *
 * AI search engines (ChatGPT, Perplexity, Claude, Gemini) rely heavily on
 * clean schema + canonical URLs, so we always emit them.
 */
export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  keywords,
  noindex = false,
  schemas = [], // array of JSON-LD objects
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Lifestyle Content for Creators, Influencers & UGC`;
  // Canonicals always point at the production host so that dev/preview pages
  // don't accidentally pollute search-engine indices with preview URLs.
  const canonical = path
    ? `${PRODUCTION_HOST}${path.startsWith("/") ? path : `/${path}`}`
    : `${PRODUCTION_HOST}/`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured data — emit each schema as its own LD+JSON block */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}

export default SEO;
